import { useContentConfig, useGlobalConfig } from '@ad-lucy/context/adData';
import { usePages } from '@ad-lucy/context/pages';
import {
  type GlobalDataQueryParams,
  HttpService,
  LocalStorageService,
  SessionService,
  type Track,
  TrackerUtils,
  type TrackingDataInterface,
  type TrackingInformation,
  UriUtils,
} from '@ad-lucy/utils';
import { useCallback } from 'react';
import { ApiConfig } from '../../../config/api';
import { useGetPayout } from '../useGetPayout';
import {
  additionalQueryTrackers,
  createTrackingElement,
  detectBotClick,
  getHomepageParameter,
  getTrackingCodeTemplate,
} from './utils';

export const useTracker = (): Track => {
  const { data: globalData } = useGlobalConfig();
  const { data: contentData } = useContentConfig();
  const { currentPage } = usePages();
  const getPayout = useGetPayout();

  const processFirstPayout = useCallback(
    (
      payout: boolean,
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      sessionData: any,
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      globalData: any,
      href: string,
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      trackingVariables: any,
    ): boolean => {
      if (sessionData.firstPayoutAlreadyFired || !payout) {
        return false;
      }

      LocalStorageService.setItem(sessionData.payoutIdentifier, 'true');

      const urlParams = new URL(href).searchParams;
      const clickTagUrl = urlParams.get('clickTag');
      // For Obi we want to implement a way to get the trackingparameters from the clickTag variable in the URL
      // For this we will check the URL and see, if we find the variable "ct=steel".
      const isUrlTrackingEnabled = urlParams.get('ct') === 'steel';

      const trackingCodeTemplate = getTrackingCodeTemplate(
        globalData,
        clickTagUrl,
        isUrlTrackingEnabled,
      );
      createTrackingElement(trackingCodeTemplate, trackingVariables, href);
      additionalQueryTrackers(location.href, 'ui-ct');

      return true;
    },
    [],
  );

  const track = useCallback(
    async (trackingInformation: TrackingInformation): Promise<void> => {
      if (!(globalData && contentData)) {
        return;
      }

      const {
        column,
        component,
        event,
        external,
        meta = {},
        mouseX = null,
        mouseY = null,
        product,
        stage,
        trigger,
      }: TrackingInformation = trackingInformation;

      const { innerHeight, innerWidth, location, outerHeight, outerWidth } = window;
      const { href } = location;

      const { admCreative, campaign } = globalData;

      const { branch } = contentData;

      const sessionData = SessionService.getSessionData(campaign);

      const params = UriUtils.getQueryParams(href) as unknown as GlobalDataQueryParams;

      const advertisementId = params['werbemittel-id'];
      const advertisementPanelId = params['flaechen-id'];

      const af = detectBotClick(trackingInformation);
      const payout = getPayout(trackingInformation);

      const trackingVariables = {
        advertisementId,
        advertisementPanelId,
        reference: params.ref,
        branchId: branch.reference,
      };

      const adVersion = globalData.version?.version ?? null;
      const first = processFirstPayout(payout, sessionData, globalData, href, trackingVariables);

      if (globalData.everyTrackingPixel && event !== 'View') {
        createTrackingElement(globalData.everyTrackingPixel, trackingVariables, href);
        additionalQueryTrackers(location.href, 'ai-ct');
      }

      if (globalData.linkoutTrackingPixel && payout && external) {
        createTrackingElement(globalData.linkoutTrackingPixel, trackingVariables, href);
        additionalQueryTrackers(location.href, 'el-ct');
      }

      const trackingData: TrackingDataInterface = {
        event,
        session: sessionData.sessionId,
        adWidth: innerWidth,
        adHeight: innerHeight,
        admCreative,
        windowWidth: outerWidth,
        windowHeight: outerHeight,
        product,
        campaign,
        branch: branch.id,
        area: advertisementPanelId,
        creative: advertisementId,
        column,
        component,
        payout,
        mouseX,
        mouseY,
        stage: stage ?? currentPage?.id,
        meta,
        trigger,
        first,
        environment: ApiConfig.environment,
        navigator: TrackerUtils.getUserAgent(),
        external,
        location: {
          lat: params.lat,
          lon: params.lon,
        },
        af,
        v: globalData.reportingSchemaVersion ?? '1',
        adVersion,
        homepage: getHomepageParameter(params),
      };

      try {
        await HttpService.post<TrackingDataInterface, void>(globalData.trackerUrl, trackingData);
      } catch (e) {
        throw new Error(`TrackingError: ${e as string}`);
      }
    },
    [globalData, contentData, currentPage, processFirstPayout, getPayout],
  );

  return track;
};
